<template>
  <div>
    <b-card v-if="uzAsboList.id">
      <b-row>
        <b-col md="8">
          <h4 class="mb-1">
            {{ uzAsboList.organization_name }}
            <b-badge
              :variant="statusInfo.variant"
              class="ml-50 align-middle"
            >
              {{ $langName(statusInfo.name) }}
            </b-badge>
          </h4>
          <b-table-simple
            small
            borderless
            class="mb-0 uz-asbo-info"
          >
            <b-tbody>
              <b-tr>
                <b-td class="text-muted">
                  {{ $t('Period') }}
                </b-td>
                <b-td>
                  <strong>{{ uzAsboList.year }} — {{ $langName(getMonthName(uzAsboList.month)) }}</strong>
                  <b-badge
                    v-if="uzAsboList.revision > 1"
                    variant="light-secondary"
                    class="ml-50"
                  >
                    {{ $t('Revision') }} {{ uzAsboList.revision }}
                  </b-badge>
                </b-td>
              </b-tr>
              <b-tr>
                <b-td class="text-muted">
                  {{ $t('INN') }}
                </b-td>
                <b-td>{{ uzAsboList.organization_inn || '—' }}</b-td>
              </b-tr>
              <b-tr>
                <b-td class="text-muted">
                  {{ $t('Researchers count') }}
                </b-td>
                <b-td><strong>{{ items.length }}</strong></b-td>
              </b-tr>
              <b-tr v-if="uzAsboList.org_head_comment">
                <b-td class="text-muted">
                  {{ $t("Responsible head comment") }}
                </b-td>
                <b-td>{{ uzAsboList.org_head_comment }}</b-td>
              </b-tr>
              <b-tr v-if="uzAsboList.innovation_comment">
                <b-td class="text-muted">
                  {{ $t('Innovation comment') }}
                </b-td>
                <b-td>{{ uzAsboList.innovation_comment }}</b-td>
              </b-tr>
              <b-tr v-if="uzAsboList.signed_payload_hash">
                <b-td class="text-muted">
                  {{ $t('Document hash') }}
                </b-td>
                <b-td>
                  <code class="small">{{ uzAsboList.signed_payload_hash }}</code>
                </b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>
        </b-col>

        <b-col
          md="4"
          class="text-md-right mt-2 mt-md-0"
        >
          <b-alert
            v-if="uzAsboList.is_frozen"
            variant="secondary"
            show
            class="p-1 text-left"
          >
            <feather-icon
              icon="LockIcon"
              size="14"
              class="mr-50"
            />
            <small>{{ $t('uz asbo frozen hint') }}</small>
          </b-alert>

          <b-button
            v-if="canEdit"
            v-b-toggle.sidebar-add-uz-asbo-item
            variant="outline-primary"
            class="mb-1"
            @click="loadCandidates"
          >
            <feather-icon
              icon="UserPlusIcon"
              class="mr-50"
            />
            {{ $t('Add researcher') }}
          </b-button>
          <br>
          <b-button
            v-if="canEdit"
            variant="primary"
            :disabled="!items.length"
            @click="send"
          >
            <feather-icon
              icon="SendIcon"
              class="mr-50"
            />
            {{ $t('Send to responsible head') }}
          </b-button>

          <template v-if="canReview">
            <b-button
              variant="success"
              class="mb-1"
              @click="review()"
            >
              <feather-icon
                icon="CheckIcon"
                class="mr-50"
              />
              {{ $t('Approve') }}
            </b-button>
            <br>
            <b-button
              variant="outline-danger"
              @click="review()"
            >
              <feather-icon
                icon="XIcon"
                class="mr-50"
              />
              {{ $t('Reject') }}
            </b-button>
          </template>
        </b-col>
      </b-row>
    </b-card>

    <b-card no-body>
      <div class="m-2 d-flex justify-content-between align-items-center">
        <h5 class="mb-0">
          {{ $t('Researchers') }}
        </h5>
        <b-form-input
          v-model="search"
          class="d-inline-block w-auto"
          :placeholder="$t('Search')"
        />
      </div>
      <b-table
        :items="filteredItems"
        :fields="visibleItemColumns"
        responsive
        show-empty
        :empty-text="$t('No matching records found')"
        primary-key="item_id"
        class="position-relative"
      >
        <template #head()="data">
          {{ $t(data.label) }}
        </template>
        <template #cell(index)="data">
          {{ data.index + 1 }}
        </template>
        <template #cell(direction_is_priority)="data">
          <b-badge :variant="data.value ? 'light-success' : 'light-secondary'">
            {{ data.value ? $t('Yes') : $t('No') }}
          </b-badge>
        </template>
        <template #cell(course)="data">
          {{ data.value || '—' }}
        </template>
        <template #cell(actions)="data">
          <feather-icon
            v-if="canEdit"
            v-b-tooltip.hover.top="$t('Remove from list')"
            icon="TrashIcon"
            class="cursor-pointer text-danger"
            size="16"
            @click="removeItem(data.item)"
          />
        </template>
      </b-table>
    </b-card>

    <!-- Izlanuvchi qo'shish -->
    <b-sidebar
      id="sidebar-add-uz-asbo-item"
      sidebar-class="sidebar-lg"
      bg-variant="white"
      shadow
      backdrop
      no-header
      right
    >
      <template #default="{ hide }">
        <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h5 class="mb-0">
            {{ $t('Add researcher') }}
          </h5>
          <feather-icon
            class="ml-1 cursor-pointer"
            icon="XIcon"
            size="16"
            @click="hide"
          />
        </div>
        <div class="p-2">
          <b-spinner
            v-if="candidatesBusy"
            variant="primary"
          />
          <p
            v-else-if="!candidates.length"
            class="text-muted"
          >
            {{ $t('All eligible researchers are already in the list') }}
          </p>
          <b-list-group v-else>
            <b-list-group-item
              v-for="candidate in candidates"
              :key="candidate.id"
              class="d-flex justify-content-between align-items-center"
            >
              <div>
                <div>{{ candidate.full_name }}</div>
                <small class="text-muted">
                  {{ candidate.pnfl }} · {{ candidate.direction_code }} · {{ candidate.dc_type_name }}
                </small>
              </div>
              <b-button
                size="sm"
                variant="outline-primary"
                @click="addItem(candidate)"
              >
                {{ $t('Add') }}
              </b-button>
            </b-list-group-item>
          </b-list-group>
        </div>
      </template>
    </b-sidebar>

    <review
      :uz-asbo-list="uzAsboList"
      :mode="reviewMode"
      @refetchData="loadList"
    />
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BBadge, BButton, BTable, BTableSimple, BTbody, BTr, BTd,
  BAlert, BSidebar, BListGroup, BListGroupItem, BSpinner, BFormInput,
  VBToggle, VBTooltip,
} from 'bootstrap-vue'
import { onUnmounted } from '@vue/composition-api'
import Ripple from 'vue-ripple-directive'
import store from '@/store'
import uzAsboModule from '@/store/uz-asbo'
import { isAdmin, isOrgHead, isRoleUniversity } from '@/auth/utils'
import Review from './Review.vue'
import config, { UZASBO_STATUSES, getMonthName, getUzAsboStatus } from './config'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BBadge,
    BButton,
    BTable,
    BTableSimple,
    BTbody,
    BTr,
    BTd,
    BAlert,
    BSidebar,
    BListGroup,
    BListGroupItem,
    BSpinner,
    BFormInput,
    Review,
  },
  directives: {
    Ripple,
    'b-toggle': VBToggle,
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      search: '',
      candidates: [],
      candidatesBusy: false,
      reviewMode: 'org-head',
    }
  },
  computed: {
    uzAsboList() {
      return this.$store.state.uzAsbo.uzAsboList || {}
    },
    items() {
      return this.uzAsboList.items || []
    },
    filteredItems() {
      const query = this.search.trim().toLowerCase()
      if (!query) return this.items
      return this.items.filter(item => `${item.full_name} ${item.pnfl} ${item.direction_code}`
        .toLowerCase().includes(query))
    },
    statusInfo() {
      return getUzAsboStatus(this.uzAsboList.status)
    },
    canEdit() {
      return isRoleUniversity() && this.uzAsboList.is_editable
    },
    canReview() {
      return isOrgHead() && this.uzAsboList.status === UZASBO_STATUSES.SENT_TO_ORG_HEAD
    },
    visibleItemColumns() {
      if (this.canEdit) return this.itemColumns
      return this.itemColumns.filter(column => column.key !== 'actions')
    },
    isInnovation() {
      return isAdmin()
    },
  },
  created() {
    this.loadList()
  },
  methods: {
    getMonthName,
    loadList() {
      this.$store.dispatch('uzAsbo/get', this.$route.params.id)
        .catch(error => this.$errorSwal(this, error))
    },
    loadCandidates() {
      this.candidatesBusy = true
      this.$store.dispatch('uzAsbo/candidates', this.uzAsboList.id)
        .then(response => {
          this.candidates = response.data
        })
        .catch(error => this.$errorSwal(this, error))
        .finally(() => {
          this.candidatesBusy = false
        })
    },
    addItem(candidate) {
      this.$store.dispatch('uzAsbo/addItem', {
        id: this.uzAsboList.id,
        applyDocId: candidate.id,
      })
        .then(() => {
          this.candidates = this.candidates.filter(item => item.id !== candidate.id)
          this.loadList()
        })
        .catch(error => this.$errorSwal(this, error))
    },
    removeItem(item) {
      this.$removeSwal(this, {
        id: this.uzAsboList.id,
        itemId: item.item_id,
      }, 'uzAsbo/removeItem', this.loadList, {
        text: `${item.full_name} ${this.$t('will be removed from the list')}`,
        confirmButtonText: 'Remove from list',
        successTitle: 'Success',
        successText: 'Successfully completed',
        errorText: 'There was an error in successful execution',
      })
    },
    send() {
      this.$removeSwal(this, this.uzAsboList.id, 'uzAsbo/send', this.loadList, {
        text: "Ro'yxat tashkilot ma'sul rahbariga yuboriladi",
        confirmButtonText: 'Send',
        successTitle: 'Sent',
        successText: 'Successfully completed',
        errorText: 'There was an error in successful execution',
      })
    },
    review() {
      // Bu sahifada tasdiqlash/rad etish faqat ma'sul rahbarga ochiq
      this.reviewMode = 'org-head'
      this.$root.$emit('bv::toggle::collapse', 'sidebar-review-uz-asbo')
    },
  },
  setup() {
    const MODULE_NAME = 'uzAsbo'
    if (!store.hasModule(MODULE_NAME)) store.registerModule(MODULE_NAME, uzAsboModule)

    onUnmounted(() => {
      if (store.hasModule(MODULE_NAME)) store.unregisterModule(MODULE_NAME)
    })

    return {
      MODULE_NAME,
      ...config(),
    }
  },
}
</script>

<style scoped>
.uz-asbo-info td {
  padding: 0.25rem 0.75rem 0.25rem 0;
}
</style>
